import { forwardRef } from 'react'
import { cn } from '@/lib/cn'

interface GlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          // Base
          'relative inline-flex items-center justify-center gap-2 font-medium',
          'rounded-2xl border transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--oc-accent)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',

          // Sizes
          size === 'sm' && 'px-3 py-1.5 text-xs',
          size === 'md' && 'px-5 py-2.5 text-sm',
          size === 'lg' && 'px-7 py-3.5 text-base',

          // Variants
          variant === 'primary' && [
            'bg-[var(--oc-accent)]/80 border-[var(--oc-accent)]/50 text-white',
            'hover:bg-[var(--oc-accent)] hover:border-[var(--oc-accent-light)]/60',
            'glow-sm',
          ],
          variant === 'secondary' && [
            'glass bg-[var(--oc-glass-bg)] border-[var(--oc-glass-border)] text-white/80',
            'hover:bg-white/10 hover:text-white',
          ],
          variant === 'ghost' && [
            'bg-transparent border-transparent text-white/70',
            'hover:bg-white/8 hover:text-white',
          ],
          variant === 'danger' && [
            'bg-red-600/70 border-red-500/50 text-white',
            'hover:bg-red-600',
          ],

          className,
        )}
        {...props}
      >
        {loading && (
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        )}
        {children}
      </button>
    )
  },
)
GlassButton.displayName = 'GlassButton'
